import React, { forwardRef } from 'react'
import clsx from 'classnames'
import Image from 'next/image'
import { useLocale } from 'use-intl'
import { Text } from '@/shared/ui'
import { Tag } from '@/shared/ui/tag'
import { ImgPlaceholder } from '@/shared/icons/img-placeholder'
import { variantsCards } from '@/shared/constants/variants'
import { IImage } from '@/interfaces/common'
import { getDateLabel } from '@/shared/utils'
import { IPostCategory } from '@/interfaces/analytics'
import { MotionLink } from '@/shared/ui/link'
import style from './analytic-card.module.scss'

export interface AnalyticCard {
  title: string
  date: string
  image: IImage | null
  index?: number
  category: IPostCategory
  slug: string | null
  type: 'local' | 'external'
  external_url: string | null
}

interface AnalyticCardProps extends Omit<React.HTMLProps<HTMLDivElement>, 'title' | 'type'>, AnalyticCard {}

const colorByCategory = {
  reports: 'main-blue',
  infographics: 'green',
  publications: 'dark',
}

export const AnalyticCard = forwardRef<HTMLAnchorElement, AnalyticCardProps>((props, ref) => {
  const { className, slug, category, image, date, title, index, external_url, type } = props
  const currentLocale = useLocale()

  return (
    <MotionLink
      ref={ref}
      href={type === 'local' ? `/analytics/${slug}` : `${external_url}`}
      className={clsx(style.container, className)}
      variants={variantsCards}
      custom={index}
      viewport={{ amount: 0.45, once: true }}
    >
      <div className={style.poster}>
        {image ? (
          <Image src={image.url} fill alt="" style={{ objectFit: 'cover', objectPosition: 'center top' }} />
        ) : (
          <ImgPlaceholder className={style.placeholder} />
        )}
      </div>
      <Text className={style.desc} type="t1" color="dark">
        {title}
      </Text>

      <footer className={style.footer}>
        <Text className={style.date} type="d" color="dark-040" tagName="span">
          {getDateLabel({ date, locale: currentLocale })}
        </Text>

        <Tag color={category.color}>{category.name}</Tag>
      </footer>
    </MotionLink>
  )
})

AnalyticCard.displayName = 'AnalyticCard'
